/**
 * HTTP Client Service with Caching and Request Deduplication
 * Provides efficient API calls with built-in caching, error handling, and retry logic
 */

import { API_CONFIG, DEFAULT_HEADERS, getAuthHeaders } from '../config/api.config';

class HttpClient {
  constructor(config = {}) {
    this.baseURL = config.baseURL || API_CONFIG.BASE_URL;
    this.cache = new Map();
    this.pendingRequests = new Map();
    this.cacheDuration = config.cacheDuration || API_CONFIG.CACHE_DURATION;
  }

  /**
   * Generate cache key from URL and params
   */
  getCacheKey(url, params = null) {
    if (params) {
      return `${url}?${new URLSearchParams(params).toString()}`;
    }
    return url;
  }

  /**
   * Get from cache if valid
   */
  getFromCache(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheDuration) {
      return cached.data;
    }
    // Clear expired cache
    this.cache.delete(key);
    return null;
  }

  /**
   * Set cache
   */
  setCache(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
    });
  }

  /**
   * Clear cache
   */
  clearCache(key = null) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Build full URL
   */
  buildUrl(endpoint, params = null) {
    const url = new URL(`${this.baseURL}${endpoint}`);
    if (params) {
      Object.keys(params).forEach(key => {
        if (params[key] !== null && params[key] !== undefined) {
          url.searchParams.append(key, params[key]);
        }
      });
    }
    return url.toString();
  }

  /**
   * Handle API errors
   */
  handleError(error) {
    console.error('API Error:', error);
    if (error.response?.status === 401) {
      // Handle unauthorized
      localStorage.removeItem('token');
      window.dispatchEvent(new Event('userLogout'));
    }
    throw error;
  }

  /**
   * GET request with caching support
   */
  async get(endpoint, params = null, options = {}) {
    const useCache = options.useCache !== false;
    const url = this.buildUrl(endpoint, params);
    const cacheKey = this.getCacheKey(endpoint, params);

    // Check cache first
    if (useCache) {
      const cached = this.getFromCache(cacheKey);
      if (cached) {
        return cached;
      }
    }

    // Return pending request if exists (request deduplication)
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey);
    }

    // Create new request
    const request = fetch(url, {
      method: 'GET',
      headers: getAuthHeaders(),
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        if (useCache) {
          this.setCache(cacheKey, data);
        }
        return data;
      })
      .catch(error => {
        this.pendingRequests.delete(cacheKey);
        this.handleError(error);
      })
      .finally(() => {
        this.pendingRequests.delete(cacheKey);
      });

    // Store pending request
    this.pendingRequests.set(cacheKey, request);
    return request;
  }

  /**
   * POST request
   */
  async post(endpoint, data = null, options = {}) {
    const url = this.buildUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: data ? JSON.stringify(data) : null,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      const result = await response.json();

      // Invalidate cache for this endpoint
      const cacheKey = this.getCacheKey(endpoint);
      this.clearCache(cacheKey);

      return result;
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * PUT request
   */
  async put(endpoint, data = null, options = {}) {
    const url = this.buildUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: data ? JSON.stringify(data) : null,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || `HTTP ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      this.handleError(error);
    }
  }

  /**
   * DELETE request
   */
  async delete(endpoint, options = {}) {
    const url = this.buildUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    } catch (error) {
      this.handleError(error);
    }
  }
}

// Export singleton instance
export const httpClient = new HttpClient();

// Helper function to reset cache
export const clearApiCache = () => {
  httpClient.clearCache();
};
